import React, { useState, useEffect, useRef } from 'react';
import './GaussianProcessDemo.css';

const GaussianProcessDemo = () => {
  // Add a constant for maximum samples
  const MAX_SAMPLES = 100;
  
  // States for different modes and data
  const [mode, setMode] = useState('drawing'); // 'drawing', 'optimizing', 'waiting', 'completed'
  const [drawnFunction, setDrawnFunction] = useState([]);
  const [sampledPoints, setSampledPoints] = useState([]);
  const [hyperparams, setHyperparams] = useState({
    lengthScale: 0.2,
    noiseLevel: 1e-6,
    nu: 2.5  // Starting with Matérn 5/2 as default
  });
  
  // For drawing and optimization
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const optimizationTimerRef = useRef(null);
  const isDrawingRef = useRef(false);
  const lastPosRef = useRef(null);
  
  // Use fixed values instead
  const ACQUISITION_FUNC = 'qLogEI'; // Set a fixed acquisition function
  const EXPLORATION_WEIGHT = 0.5; // Fixed exploration weight
  
  // Drawing handlers - completely revised approach
  const handleMouseDown = (e) => {
    if (mode !== 'drawing') return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    
    // Get coordinates
    const x = (e.clientX - rect.left) / rect.width;
    const y = 1 - (e.clientY - rect.top) / rect.height;
    
    // Start drawing
    isDrawingRef.current = true;
    lastPosRef.current = { x, y };
    
    // Create a segment-based grid for the drawn function
    const gridSize = 100; // Fixed number of x segments
    const newPoints = Array(gridSize).fill(null);
    
    // Start with any existing function data
    if (drawnFunction.length > 1) {
      const sortedPoints = [...drawnFunction].sort((a, b) => a.x - b.x);
      
      // Populate the grid based on existing points
      sortedPoints.forEach(point => {
        const index = Math.floor(point.x * (gridSize - 1));
        if (index >= 0 && index < gridSize) {
          newPoints[index] = point.y;
        }
      });
    }
    
    // Add first point
    const index = Math.floor(x * (gridSize - 1));
    if (index >= 0 && index < gridSize) {
      newPoints[index] = y;
    }
    
    // Update immediately
    updateDrawnFunction(newPoints);
    
    // Draw
    drawFunction(newPoints);
  };
  
  const handleMouseMove = (e) => {
    if (!isDrawingRef.current || mode !== 'drawing') return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    
    // Get coordinates
    const x = (e.clientX - rect.left) / rect.width;
    const y = 1 - (e.clientY - rect.top) / rect.height;
    
    // Get current drawn function as grid
    const gridSize = 100;
    let newPoints = Array(gridSize).fill(null);
    
    if (drawnFunction.length > 0) {
      drawnFunction.forEach(point => {
        const index = Math.floor(point.x * (gridSize - 1));
        if (index >= 0 && index < gridSize) {
          newPoints[index] = point.y;
        }
      });
    }
    
    // Interpolate between last position and current position
    if (lastPosRef.current) {
      const { x: lastX, y: lastY } = lastPosRef.current;
      
      // Calculate distance
      const dist = Math.sqrt(Math.pow(x - lastX, 2) + Math.pow(y - lastY, 2));
      
      // Add intermediate points for smoother curves
      const steps = Math.max(1, Math.ceil(dist * 50)); // More steps for larger distances
      
      for (let i = 0; i <= steps; i++) {
        const ratio = i / steps;
        const interpX = lastX + (x - lastX) * ratio;
        const interpY = lastY + (y - lastY) * ratio;
        
        // Place in grid
        const index = Math.floor(interpX * (gridSize - 1));
        if (index >= 0 && index < gridSize) {
          newPoints[index] = interpY;
        }
      }
    }
    
    // Update last position
    lastPosRef.current = { x, y };
    
    // Update drawn function
    updateDrawnFunction(newPoints);
    
    // Draw
    drawFunction(newPoints);
  };
  
  const handleMouseUp = () => {
    isDrawingRef.current = false;
  };
  
  // Helper to convert grid to points array
  const updateDrawnFunction = (grid) => {
    const points = [];
    const gridSize = grid.length;
    
    for (let i = 0; i < gridSize; i++) {
      if (grid[i] !== null) {
        points.push({
          x: i / (gridSize - 1),
          y: grid[i]
        });
      }
    }
    
    setDrawnFunction(points);
  };
  
  // Draw the function directly
  const drawFunction = (grid) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;
    
    // Clear the canvas
    ctx.fillStyle = '#fcfcfc';
    ctx.fillRect(0, 0, width, height);
    
    // Draw the points
    const gridSize = grid.length;
    const points = [];
    
    for (let i = 0; i < gridSize; i++) {
      if (grid[i] !== null) {
        points.push({
          x: i / (gridSize - 1),
          y: grid[i]
        });
      }
    }
    
    if (points.length > 1) {
      // Get function domain bounds for highlighting
      const bounds = getFunctionBounds(points);
      
      // Highlight the function domain with a subtle background
      ctx.fillStyle = 'rgba(240, 240, 250, 0.5)';
      ctx.fillRect(
        bounds.min * width, 0, 
        (bounds.max - bounds.min) * width, height
      );
      
      // Draw function
      ctx.strokeStyle = '#444444';
      ctx.lineWidth = 2;
      ctx.beginPath();
      
      // Sort by x value
      const sortedPoints = [...points].sort((a, b) => a.x - b.x);
      
      sortedPoints.forEach((point, i) => {
        const x = point.x * width;
        const y = height * (1 - point.y);
        
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      });
      ctx.stroke();
    }
    
    // Draw instructions
    ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
    ctx.font = '14px Arial';
    ctx.textAlign = 'center';
    
    if (mode === 'drawing' && drawnFunction.length === 0) {
      // More prominent initial instructions
      ctx.fillStyle = 'rgba(0, 0, 0, 0.8)';
      ctx.font = 'bold 16px Arial';
      ctx.fillText('Draw a function by clicking', width / 2, height / 2 - 30);
      
      // Visual hint - drawing arrow
      ctx.strokeStyle = 'rgba(0, 0, 0, 0.4)';
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 3]);
      
      // Draw a wavy line to suggest drawing
      ctx.beginPath();
      const startX = width * 0.3;
      const endX = width * 0.7;
      const midY = height / 2 + 30;
      const waveHeight = 20;
      
      ctx.moveTo(startX, midY);
      for (let x = startX; x <= endX; x += 10) {
        const progress = (x - startX) / (endX - startX);
        const y = midY + Math.sin(progress * Math.PI * 3) * waveHeight;
        ctx.lineTo(x, y);
      }
      ctx.stroke();
      
      // Reset line dash
      ctx.setLineDash([]);
    } else if (mode === 'completed') {
      ctx.fillText('Optimization completed with ' + sampledPoints.length + ' samples', width / 2, 25);
      if (sampledPoints.length > 0) {
        const bestPoint = [...sampledPoints].sort((a, b) => b.y - a.y)[0];
        ctx.fillText(`Best value: ${bestPoint.y.toFixed(3)} at x = ${bestPoint.x.toFixed(3)}`, width / 2, 50);
      }
    }
    
    // Find and mark the optimum of the drawn function
    if (drawnFunction.length > 2) {
      // Convert drawnFunction to evaluation points
      const evaluationPoints = [];
      for (let x = 0; x <= 1; x += 0.01) {
        const bounds = getFunctionBounds(drawnFunction);
        if (x >= bounds.min && x <= bounds.max) {
          evaluationPoints.push({
            x: x,
            y: evaluateDrawnFunction(x)
          });
        }
      }
      
      // Find the maximum point
      const maxPoint = evaluationPoints.reduce((max, point) => 
        point.y > max.y ? point : max
      , evaluationPoints[0]);
      
      // Draw a golden star at the optimum
      const optX = maxPoint.x * width;
      const optY = height * (1 - maxPoint.y);
      
      // Draw a star
      drawStar(ctx, optX, optY, 8, 'gold', 'rgba(0, 0, 0, 0.5)');
      
      // Add annotation label for true maximum
      ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
      ctx.font = 'bold 12px Arial';
      
      // Position label above and to the right of the star
      const labelX = optX + 15;
      const labelY = optY - 15;
      const labelText = 'True Maximum';
      
      // Draw annotation line
      ctx.strokeStyle = 'rgba(0, 0, 0, 0.5)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(optX, optY);
      ctx.lineTo(labelX - 5, labelY + 6);
      ctx.stroke();
      
      // Remove background for cleaner look
      // Draw text directly
      ctx.fillStyle = 'rgba(0, 0, 0, 0.8)';
      ctx.fillText(labelText, labelX, labelY);
    }
  };
  
  // Start Bayesian optimization
  const startOptimization = () => {
    if (drawnFunction.length < 10) {
      alert("Please draw a more complete function before starting optimization");
      return;
    }
    
    // Process the drawn function to ensure uniqueness in x values
    const processedFunction = processDrawnFunction(drawnFunction);
    setDrawnFunction(processedFunction);
    
    // Reset optimization and set to waiting mode
    setMode('waiting');
    
    // Get function domain bounds
    const bounds = getFunctionBounds(processedFunction);
    
    // Start with multiple points for better coverage
    const initialPoints = [];
    
    // Add 3 initial points: near left boundary, middle, and near right boundary
    const leftX = bounds.min + (bounds.max - bounds.min) * 0.1;
    const middleX = bounds.min + (bounds.max - bounds.min) * 0.5;
    const rightX = bounds.min + (bounds.max - bounds.min) * 0.9;
    
    initialPoints.push({ x: leftX, y: evaluateDrawnFunction(leftX) });
    initialPoints.push({ x: middleX, y: evaluateDrawnFunction(middleX) });
    initialPoints.push({ x: rightX, y: evaluateDrawnFunction(rightX) });
    
    setSampledPoints(initialPoints);
  };
  
  // Process drawn function to ensure each x has only one y value
  const processDrawnFunction = (points) => {
    // Sort by x value
    const sorted = [...points].sort((a, b) => a.x - b.x);
    
    // Map to keep track of unique x values (taking the latest y for each x)
    const uniquePoints = new Map();
    
    sorted.forEach(point => {
      uniquePoints.set(point.x, point.y);
    });
    
    // Convert back to array of points
    return Array.from(uniquePoints.entries()).map(([x, y]) => ({ x, y }));
  };
  
  // Get min and max bounds of the function domain
  const getFunctionBounds = (points) => {
    if (points.length === 0) return { min: 0, max: 1 };
    
    const xValues = points.map(p => p.x);
    return {
      min: Math.min(...xValues),
      max: Math.max(...xValues)
    };
  };
  
  // Modify addNextSample to check against MAX_SAMPLES
  const addNextSample = () => {
    // Only proceed if we're in optimization or waiting mode and haven't reached max samples
    if (mode !== 'optimizing' && mode !== 'waiting') return;
    if (sampledPoints.length >= MAX_SAMPLES) {
      setMode('completed');
      return;
    }
    
    // Rest of the function remains the same
    setMode('optimizing');
    
    // Get function domain bounds
    const bounds = getFunctionBounds(drawnFunction);
    
    // Implement an epsilon-greedy strategy (occasionally pick a random point)
    const useRandom = Math.random() < 0.1; // 10% chance for pure exploration
    
    if (useRandom) {
      // Pure exploration - sample a random point, favoring unexplored regions
      const existingXs = sampledPoints.map(p => p.x);
      const numCandidates = 50;
      const candidates = [];
      
      // Generate random candidates
      for (let i = 0; i < numCandidates; i++) {
        candidates.push(bounds.min + Math.random() * (bounds.max - bounds.min));
      }
      
      // Find the candidate furthest from existing points
      let bestX = candidates[0];
      let maxMinDist = -Infinity;
      
      candidates.forEach(x => {
        // Find minimum distance to any existing point
        const minDist = Math.min(...existingXs.map(existingX => Math.abs(x - existingX)));
        if (minDist > maxMinDist) {
          maxMinDist = minDist;
          bestX = x;
        }
      });
      
      // Evaluate function at the chosen point
      const y = evaluateDrawnFunction(bestX);
      
      // Add the new point
      setSampledPoints(prev => [...prev, { x: bestX, y }]);
      setMode('waiting');
      return;
    }
    
    // Regular acquisition-based selection with explicit exploration component
    const numCandidates = 100;
    const candidates = Array.from(
      { length: numCandidates }, 
      (_, i) => bounds.min + (i / (numCandidates - 1)) * (bounds.max - bounds.min)
    );
    
    // Calculate both acquisition value and diversity score for each candidate
    const candidateScores = candidates.map(x => {
      // Regular acquisition value
      const acqValue = calculateAcquisition(x, sampledPoints, hyperparams, ACQUISITION_FUNC);
      
      // Diversity score (distance to nearest sample)
      const distances = sampledPoints.map(p => Math.abs(x - p.x));
      const minDistance = Math.min(...distances);
      const normalizedDistance = minDistance / (bounds.max - bounds.min);
      
      // Combined score with exploration weight
      return {
        x,
        score: EXPLORATION_WEIGHT * acqValue + (1 - EXPLORATION_WEIGHT) * normalizedDistance
      };
    });
    
    // Pick point with highest combined score
    candidateScores.sort((a, b) => b.score - a.score);
    const bestX = candidateScores[0].x;
    
    // Evaluate function at the chosen point
    const y = evaluateDrawnFunction(bestX);
    
    // Add the new point
    setSampledPoints(prev => {
      const newPoints = [...prev, { x: bestX, y }];
      
      // Update hyperparameters
      if (newPoints.length >= 2) {
        const optParams = optimizeHyperparameters(newPoints);
        setHyperparams(optParams);
      }
      
      // Always go back to waiting mode after optimization
      setMode('waiting');
      
      return newPoints;
    });
  };
  
  // Evaluate the drawn function at a given x
  const evaluateDrawnFunction = (x) => {
    if (drawnFunction.length === 0) return 0.5;
    
    // Sort drawn points by x value
    const sortedPoints = [...drawnFunction].sort((a, b) => a.x - b.x);
    
    // If x is outside the drawn range, return the closest end point
    if (x <= sortedPoints[0].x) return sortedPoints[0].y;
    if (x >= sortedPoints[sortedPoints.length - 1].x) return sortedPoints[sortedPoints.length - 1].y;
    
    // Find the two points that surround x
    for (let i = 0; i < sortedPoints.length - 1; i++) {
      if (sortedPoints[i].x <= x && x <= sortedPoints[i+1].x) {
        // Linear interpolation
        const p1 = sortedPoints[i];
        const p2 = sortedPoints[i+1];
        const ratio = (x - p1.x) / (p2.x - p1.x);
        return p1.y * (1 - ratio) + p2.y * ratio;
      }
    }
    
    return 0.5; // Fallback
  };
  
  // Advanced Matérn kernel optimization based on research
  const optimizeHyperparameters = (points) => {
    if (points.length < 2) return { 
      lengthScale: 0.2, 
      noiseLevel: 1e-6,
      nu: 2.5  // Default to Matérn 5/2
    };
    
    // Sort points by x
    const sortedPoints = [...points].sort((a, b) => a.x - b.x);
    
    // Approximate log marginal likelihood calculation for different nu values
    const evaluateLogLikelihood = (params) => {
      const { lengthScale, nu } = params;
      const n = points.length;
      
      // Build kernel matrix for these parameters
      const K = Array(n).fill().map(() => Array(n).fill(0));
      
      // Build the covariance matrix
      for (let i = 0; i < n; i++) {
        for (let j = 0; j <= i; j++) {
          const dist = Math.abs(points[i].x - points[j].x);
          let kValue;
          
          // Matérn kernel based on nu value
          if (nu === 0.5) {
            // Matérn 1/2
            kValue = Math.exp(-dist / lengthScale);
          } else if (nu === 1.5) {
            // Matérn 3/2
            const arg = Math.sqrt(3) * dist / lengthScale;
            kValue = (1 + arg) * Math.exp(-arg);
          } else if (nu === 2.5) {
            // Matérn 5/2
            const arg = Math.sqrt(5) * dist / lengthScale;
            kValue = (1 + arg + (arg * arg) / 3) * Math.exp(-arg);
          } else {
            // Default to squared exponential for other values
            kValue = Math.exp(-0.5 * Math.pow(dist / lengthScale, 2));
          }
          
          K[i][j] = kValue;
          if (i !== j) K[j][i] = kValue;
        }
        
        // Add tiny noise for numerical stability
        K[i][i] += 1e-6;
      }
      
      // Extract y values
      const y = points.map(p => p.y);
      
      try {
        // Calculate log determinant using Cholesky decomposition
        const L = choleskyDecomposition(K);
        let logDet = 0;
        for (let i = 0; i < n; i++) {
          logDet += 2 * Math.log(L[i][i]);
        }
        
        // Solve K⁻¹y
        const alpha = choleskyAndSolve(K, y);
        
        // Calculate data fit term: -0.5 * y^T K⁻¹ y
        let dataFit = 0;
        for (let i = 0; i < n; i++) {
          dataFit -= 0.5 * y[i] * alpha[i];
        }
        
        // Calculate log marginal likelihood: data fit - 0.5 * log|K| - 0.5 * n * log(2π)
        const logML = dataFit - 0.5 * logDet - 0.5 * n * Math.log(2 * Math.PI);
        return logML;
      } catch (e) {
        // If Cholesky fails (non-positive definite matrix), return very negative score
        return -Infinity;
      }
    };
    
    // Helper for Cholesky decomposition
    const choleskyDecomposition = (A) => {
      const n = A.length;
      const L = Array(n).fill().map(() => Array(n).fill(0));
      
      for (let i = 0; i < n; i++) {
        for (let j = 0; j <= i; j++) {
          let sum = 0;
          for (let k = 0; k < j; k++) {
            sum += L[i][k] * L[j][k];
          }
          
          if (i === j) {
            const val = A[i][i] - sum;
            if (val <= 0) throw new Error("Matrix not positive definite");
            L[i][j] = Math.sqrt(val);
          } else {
            L[i][j] = (A[i][j] - sum) / L[j][j];
          }
        }
      }
      
      return L;
    };
    
    // Calculate spectral density diagnostic - estimate function roughness 
    // using techniques from Rasmussen & Williams
    const estimateRoughness = () => {
      // Need at least 5 points for reliable estimates
      if (points.length < 5) return "medium";
      
      // Calculate finite differences
      const diffs = [];
      for (let i = 1; i < sortedPoints.length; i++) {
        const dx = sortedPoints[i].x - sortedPoints[i-1].x;
        const dy = sortedPoints[i].y - sortedPoints[i-1].y;
        if (dx > 0) diffs.push(Math.abs(dy / dx));
      }
      
      // Sort differences
      diffs.sort((a, b) => a - b);
      
      // Calculate median rate of change
      const medianDiff = diffs[Math.floor(diffs.length / 2)];
      
      // Also look at 75th percentile for non-Gaussian behavior
      const p75Diff = diffs[Math.floor(diffs.length * 0.75)];
      
      // Calculate ratio between 75th and 50th percentile
      // High ratio indicates heavy tails / non-Gaussian behavior
      const diffRatio = p75Diff / medianDiff;
      
      // Use ratio and median to classify roughness
      if (medianDiff < 1.0) {
        if (diffRatio < 2.0) return "very_smooth";
        return "smooth";
      } else if (medianDiff < 3.0) {
        if (diffRatio > 3.0) return "rough";
        return "medium";
      } else {
        if (diffRatio > 3.0) return "very_rough";
        return "rough";
      }
    };
    
    // Based on Gramacy, Flaxman and others' research on GP kernel selection
    const roughnessCategory = estimateRoughness();
    
    // Set initial nu and length scale bounds based on roughness
    let nuCandidates = [0.5, 1.5, 2.5]; // Matérn 1/2, 3/2, 5/2
    let lengthScaleCandidates = [];
    
    // Calculate average distance between points
    let avgXDist = 0;
    for (let i = 1; i < sortedPoints.length; i++) {
      avgXDist += Math.abs(sortedPoints[i].x - sortedPoints[i-1].x);
    }
    avgXDist /= (sortedPoints.length - 1);
    
    // Create grid of length scales based on roughness
    // eslint-disable-next-line
    switch (roughnessCategory) {
      case "very_rough":
        // For very rough functions, prefer lower nu values
        nuCandidates = [0.5, 1.5];
        // Shorter length scales for rougher functions
        lengthScaleCandidates = [
          avgXDist * 0.5, avgXDist * 1.0, avgXDist * 2.0
        ];
        break;
      case "rough":
        nuCandidates = [0.5, 1.5, 2.5];
        lengthScaleCandidates = [
          avgXDist * 1.0, avgXDist * 2.0, avgXDist * 3.0
        ];
        break;
      case "medium":
        nuCandidates = [1.5, 2.5];
        lengthScaleCandidates = [
          avgXDist * 1.5, avgXDist * 3.0, avgXDist * 4.5
        ];
        break;
      case "smooth":
        nuCandidates = [1.5, 2.5];
        lengthScaleCandidates = [
          avgXDist * 2.0, avgXDist * 4.0, avgXDist * 6.0
        ];
        break;
      case "very_smooth":
        // For very smooth functions, prefer higher nu values
        nuCandidates = [2.5];
        lengthScaleCandidates = [
          avgXDist * 3.0, avgXDist * 6.0, avgXDist * 9.0
        ];
        break;
    }
    
    // Ensure all length scales are within reasonable bounds
    lengthScaleCandidates = lengthScaleCandidates.map(
      ls => Math.max(0.05, Math.min(0.5, ls))
    );
    
    // Grid search over nu and length scale
    let bestLogLikelihood = -Infinity;
    let bestParams = { lengthScale: 0.2, noiseLevel: 1e-6, nu: 1.5 };
    
    // Try all combinations of nu and lengthScale
    for (const nu of nuCandidates) {
      for (const lengthScale of lengthScaleCandidates) {
        const params = { lengthScale, nu };
        const logLikelihood = evaluateLogLikelihood(params);
        
        if (logLikelihood > bestLogLikelihood) {
          bestLogLikelihood = logLikelihood;
          bestParams = { 
            lengthScale, 
            noiseLevel: 1e-6, 
            nu 
          };
        }
      }
    }
    
    // Use leave-one-out cross-validation to verify
    if (points.length >= 5) {
      // Calculate prediction error for the best parameters
      let totalSquaredError = 0;
      
      for (let i = 0; i < points.length; i++) {
        // Create a dataset leaving out point i
        const trainingPoints = points.filter((_, j) => j !== i);
        
        // Predict point i
        const { mean } = predictWithParams(
          points[i].x, trainingPoints, bestParams
        );
        
        // Accumulate squared error
        totalSquaredError += Math.pow(mean - points[i].y, 2);
      }
      
      // If cross-validation error is high, adjust parameters
      const rmse = Math.sqrt(totalSquaredError / points.length);
      if (rmse > 0.1) {
        // If prediction error is high, try to make the model more flexible
        if (bestParams.nu > 0.5) bestParams.nu = 0.5;
        bestParams.lengthScale = Math.max(0.05, bestParams.lengthScale * 0.7);
      }
    }
    
    return bestParams;
  };
  
  // Helper function for leave-one-out cross-validation
  const predictWithParams = (x, points, params) => {
    const { lengthScale, noiseLevel, nu } = params;
    const n = points.length;
    
    // Matérn kernel function
    const maternKernel = (x1, x2) => {
      const dist = Math.abs(x1 - x2);
      
      // Different versions of the Matérn kernel based on nu
      if (nu === 0.5) { // nu = 1/2 (exponential kernel)
        return Math.exp(-dist / lengthScale);
      } else if (nu === 1.5) { // nu = 3/2
        const sqrt3 = Math.sqrt(3);
        const arg = sqrt3 * dist / lengthScale;
        return (1 + arg) * Math.exp(-arg);
      } else if (nu === 2.5) { // nu = 5/2
        const sqrt5 = Math.sqrt(5);
        const arg = sqrt5 * dist / lengthScale;
        return (1 + arg + (arg * arg) / 3) * Math.exp(-arg);
      } else { // Default to squared exponential
        return Math.exp(-0.5 * Math.pow(dist / lengthScale, 2));
      }
    };
    
    // Build kernel matrix
    const K = Array(n).fill().map(() => Array(n).fill(0));
    const kStar = Array(n).fill(0);
    const jitter = 1e-6;
    
    // Build the covariance matrix K
    for (let i = 0; i < n; i++) {
      for (let j = 0; j <= i; j++) {
        const kValue = maternKernel(points[i].x, points[j].x);
        K[i][j] = kValue;
        if (i !== j) K[j][i] = kValue; // Exploit symmetry
      }
      // Add noise to diagonal
      K[i][i] += Math.pow(noiseLevel, 2) + jitter;
      
      // Calculate kernel vector elements
      kStar[i] = maternKernel(x, points[i].x);
    }
    
    // Extract y values and normalize them to improve numerical stability
    const y = points.map(p => p.y);
    
    // Compute mean: μ* = k*ᵀ K⁻¹ y
    const alpha = choleskyAndSolve(K, y);
    let mean = 0;
    for (let i = 0; i < n; i++) {
      mean += kStar[i] * alpha[i];
    }
    
    // Compute variance: σ²* = k(x*,x*) - k*ᵀ K⁻¹ k*
    // k(x*,x*) = 1 for the squared exponential kernel with scale=1
    const beta = choleskyAndSolve(K, kStar);
    
    // Prior variance (kernel evaluated at [x,x])
    const kStarStar = 1.0 + jitter;
    
    // Calculate posterior variance with protection against numerical errors
    let variance = kStarStar;
    for (let i = 0; i < n; i++) {
      variance -= kStar[i] * beta[i];
    }
    
    // Enforce a minimum positive variance to avoid numerical issues
    variance = Math.max(0.001, variance);
    
    return { mean, variance };
  };
  
  // Updated GP prediction with adaptive Matérn kernel
  const predictGP = (x, points, params) => {
    if (points.length === 0) return { mean: 0.5, variance: 0.25 };
    
    const { lengthScale, noiseLevel, nu } = params;
    const n = points.length;
    
    // Matérn kernel implementation 
    const maternKernel = (x1, x2) => {
      const dist = Math.abs(x1 - x2);
      
      // Different versions of the Matérn kernel based on nu
      if (nu === 0.5) { // nu = 1/2 (exponential kernel)
        return Math.exp(-dist / lengthScale);
      } else if (nu === 1.5) { // nu = 3/2
        const sqrt3 = Math.sqrt(3);
        const arg = sqrt3 * dist / lengthScale;
        return (1 + arg) * Math.exp(-arg);
      } else if (nu === 2.5) { // nu = 5/2
        const sqrt5 = Math.sqrt(5);
        const arg = sqrt5 * dist / lengthScale;
        return (1 + arg + (arg * arg) / 3) * Math.exp(-arg);
      } else { // Default to squared exponential
        return Math.exp(-0.5 * Math.pow(dist / lengthScale, 2));
      }
    };
    
    // Build kernel matrix
    const K = Array(n).fill().map(() => Array(n).fill(0));
    const kStar = Array(n).fill(0);
    const jitter = 1e-6;
    
    // Build the covariance matrix K
    for (let i = 0; i < n; i++) {
      for (let j = 0; j <= i; j++) {
        const kValue = maternKernel(points[i].x, points[j].x);
        K[i][j] = kValue;
        if (i !== j) K[j][i] = kValue; // Exploit symmetry
      }
      // Add noise to diagonal
      K[i][i] += Math.pow(noiseLevel, 2) + jitter;
      
      // Calculate kernel vector elements
      kStar[i] = maternKernel(x, points[i].x);
    }
    
    // Extract y values and normalize them to improve numerical stability
    const y = points.map(p => p.y);
    
    // Compute mean: μ* = k*ᵀ K⁻¹ y
    const alpha = choleskyAndSolve(K, y);
    let mean = 0;
    for (let i = 0; i < n; i++) {
      mean += kStar[i] * alpha[i];
    }
    
    // Compute variance: σ²* = k(x*,x*) - k*ᵀ K⁻¹ k*
    // k(x*,x*) = 1 for the squared exponential kernel with scale=1
    const beta = choleskyAndSolve(K, kStar);
    
    // Prior variance (kernel evaluated at [x,x])
    const kStarStar = 1.0 + jitter;
    
    // Calculate posterior variance with protection against numerical errors
    let variance = kStarStar;
    for (let i = 0; i < n; i++) {
      variance -= kStar[i] * beta[i];
    }
    
    // Enforce a minimum positive variance to avoid numerical issues
    variance = Math.max(0.001, variance);
    
    return { mean, variance };
  };
  
  // Improved matrix solver using Cholesky decomposition with fallback
  const choleskyAndSolve = (A, b) => {
    const n = A.length;
    
    try {
      // Cholesky decomposition: A = L*L^T
      const L = Array(n).fill().map(() => Array(n).fill(0));
      
      for (let i = 0; i < n; i++) {
        for (let j = 0; j <= i; j++) {
          let sum = 0;
          for (let k = 0; k < j; k++) {
            sum += L[i][k] * L[j][k];
          }
          
          if (i === j) {
            const val = A[i][i] - sum;
            if (val <= 0) throw new Error("Matrix not positive definite");
            L[i][j] = Math.sqrt(val);
          } else {
            L[i][j] = (A[i][j] - sum) / L[j][j];
          }
        }
      }
      
      // Forward substitution to solve L*y = b
      const y = Array(n).fill(0);
      for (let i = 0; i < n; i++) {
        let sum = 0;
        for (let j = 0; j < i; j++) {
          sum += L[i][j] * y[j];
        }
        y[i] = (b[i] - sum) / L[i][i];
      }
      
      // Backward substitution to solve L^T*x = y
      const x = Array(n).fill(0);
      for (let i = n - 1; i >= 0; i--) {
        let sum = 0;
        for (let j = i + 1; j < n; j++) {
          sum += L[j][i] * x[j];
        }
        x[i] = (y[i] - sum) / L[i][i];
      }
      
      return x;
    } catch (e) {
      // Fallback to more robust methods if Cholesky fails
      return robustSolve(A, b);
    }
  };
  
  // Fallback robust solver for ill-conditioned matrices
  const robustSolve = (A, b) => {
    const n = A.length;
    
    // Add regularization to diagonal
    const regA = A.map((row, i) => [...row]);
    for (let i = 0; i < n; i++) {
      regA[i][i] += 1e-8;
    }
    
    // Simple Gaussian elimination with partial pivoting
    for (let i = 0; i < n; i++) {
      // Find pivot
      let maxRow = i;
      let maxVal = Math.abs(regA[i][i]);
      
      for (let j = i + 1; j < n; j++) {
        const absVal = Math.abs(regA[j][i]);
        if (absVal > maxVal) {
          maxVal = absVal;
          maxRow = j;
        }
      }
      
      // Swap rows if needed
      if (maxRow !== i) {
        [regA[i], regA[maxRow]] = [regA[maxRow], regA[i]];
        [b[i], b[maxRow]] = [b[maxRow], b[i]];
      }
      
      // Eliminate below
      for (let j = i + 1; j < n; j++) {
        const factor = regA[j][i] / regA[i][i];
        b[j] -= factor * b[i];
        
        for (let k = i; k < n; k++) {
          regA[j][k] -= factor * regA[i][k];
        }
      }
    }
    
    // Back substitution
    const x = Array(n).fill(0);
    for (let i = n - 1; i >= 0; i--) {
      let sum = 0;
      for (let j = i + 1; j < n; j++) {
        sum += regA[i][j] * x[j];
      }
      x[i] = (b[i] - sum) / regA[i][i];
    }
    
    return x;
  };
  
  // Reset function
  const resetDemo = () => {
    if (optimizationTimerRef.current) {
      clearTimeout(optimizationTimerRef.current);
    }
    setMode('drawing');
    setDrawnFunction([]);
    setSampledPoints([]);
  };
  
  // Draw everything
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;
    
    // Clear canvas
    ctx.fillStyle = '#fcfcfc';
    ctx.fillRect(0, 0, width, height);
    
    // Draw user function if any
    if (drawnFunction.length > 1) {
      // Get function domain bounds for highlighting
      const bounds = getFunctionBounds(drawnFunction);
      
      // Highlight the function domain with a subtle background
      ctx.fillStyle = 'rgba(240, 240, 250, 0.5)';
      ctx.fillRect(
        bounds.min * width, 0, 
        (bounds.max - bounds.min) * width, height
      );
      
      // Draw domain boundaries
      ctx.strokeStyle = 'rgba(0, 0, 0, 0.1)';
      ctx.setLineDash([5, 3]);
      ctx.lineWidth = 1;
      
      ctx.beginPath();
      ctx.moveTo(bounds.min * width, 0);
      ctx.lineTo(bounds.min * width, height);
      ctx.stroke();
      
      ctx.beginPath();
      ctx.moveTo(bounds.max * width, 0);
      ctx.lineTo(bounds.max * width, height);
      ctx.stroke();
      
      ctx.setLineDash([]);
      
      // Draw function
      ctx.strokeStyle = '#444444';
      ctx.lineWidth = 2;
      ctx.beginPath();
      
      drawnFunction.forEach((point, i) => {
        const x = point.x * width;
        const y = height * (1 - point.y);
        
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      });
      ctx.stroke();
      
      // Find and mark the optimum of the drawn function
      if (drawnFunction.length > 2) {
        // Convert drawnFunction to evaluation points
        const evaluationPoints = [];
        for (let x = 0; x <= 1; x += 0.01) {
          const bounds = getFunctionBounds(drawnFunction);
          if (x >= bounds.min && x <= bounds.max) {
            evaluationPoints.push({
              x: x,
              y: evaluateDrawnFunction(x)
            });
          }
        }
        
        // Find the maximum point
        const maxPoint = evaluationPoints.reduce((max, point) => 
          point.y > max.y ? point : max
        , evaluationPoints[0]);
        
        // Draw a golden star at the optimum
        const optX = maxPoint.x * width;
        const optY = height * (1 - maxPoint.y);
        
        // Draw a star
        drawStar(ctx, optX, optY, 8, 'gold', 'rgba(0, 0, 0, 0.5)');
        
        // Add annotation label for true maximum
        ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
        ctx.font = 'bold 12px Arial';
        
        // Position label above and to the right of the star
        const labelX = optX + 15;
        const labelY = optY - 15;
        const labelText = 'True Maximum';
        
        // Draw annotation line
        ctx.strokeStyle = 'rgba(0, 0, 0, 0.5)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(optX, optY);
        ctx.lineTo(labelX - 5, labelY + 6);
        ctx.stroke();
        
        // Remove background for cleaner look
        // Draw text directly
        ctx.fillStyle = 'rgba(0, 0, 0, 0.8)';
        ctx.fillText(labelText, labelX, labelY);
      }
    }
    
    // Add prominent instructions when canvas is empty
    if (mode === 'drawing' && drawnFunction.length === 0) {
      // More prominent initial instructions
      ctx.fillStyle = 'rgba(0, 0, 0, 0.8)';
      ctx.font = 'bold 16px Arial';
      ctx.fillText('Draw a function by clicking', width / 2, height / 2 - 30);
   
      // Visual hint - drawing arrow
      ctx.strokeStyle = 'rgba(0, 0, 0, 0.4)';
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 3]);
      
      // Draw a wavy line to suggest drawing
      ctx.beginPath();
      const startX = width * 0.3;
      const endX = width * 0.7;
      const midY = height / 2 + 30;
      const waveHeight = 20;
      
      ctx.moveTo(startX, midY);
      for (let x = startX; x <= endX; x += 10) {
        const progress = (x - startX) / (endX - startX);
        const y = midY + Math.sin(progress * Math.PI * 3) * waveHeight;
        ctx.lineTo(x, y);
      }
      ctx.stroke();
      
      // Reset line dash
      ctx.setLineDash([]);
    }
    
    // Draw GP and acquisition if in optimization, waiting, or completed mode
    if (mode === 'optimizing' || mode === 'waiting' || mode === 'completed') {
      const xValues = Array.from({ length: 100 }, (_, i) => i / 99);
      
      // Get predictions
      const predictions = xValues.map(x => ({
        x,
        ...predictGP(x, sampledPoints, hyperparams)
      }));
      
      // Draw uncertainty region
      ctx.fillStyle = 'rgba(65, 105, 225, 0.1)';
      ctx.beginPath();
      
      // Upper bound
      predictions.forEach((pred, i) => {
        const x = pred.x * width;
        const y = height * (1 - (pred.mean + 2 * Math.sqrt(pred.variance)));
        
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      });
      
      // Lower bound
      for (let i = predictions.length - 1; i >= 0; i--) {
        const pred = predictions[i];
        const x = pred.x * width;
        const y = height * (1 - (pred.mean - 2 * Math.sqrt(pred.variance)));
        ctx.lineTo(x, y);
      }
      
      ctx.closePath();
      ctx.fill();
      
      // Draw mean function
      ctx.strokeStyle = '#4169E1';
      ctx.lineWidth = 2;
      ctx.beginPath();
      
      predictions.forEach((pred, i) => {
        const x = pred.x * width;
        const y = height * (1 - pred.mean);
        
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      });
      ctx.stroke();

      // Calculate and draw acquisition function if we have enough points
      if (sampledPoints.length >= 3) {
        // Get function domain bounds
        const bounds = getFunctionBounds(drawnFunction);
        
        // Calculate acquisition values for each x
        const acquisitions = xValues.map(x => ({
          x,
          value: calculateAcquisition(x, sampledPoints, hyperparams, ACQUISITION_FUNC),
          // Flag points that are within the valid domain
          inDomain: (x >= bounds.min && x <= bounds.max)
        }));
        
        // Find min and max for normalization
        const acqValues = acquisitions.map(a => a.value);
        const minAcq = Math.min(...acqValues);
        const maxAcq = Math.max(...acqValues);
        
        // Find the optimum point, but only consider points within the domain
        const domainAcquisitions = acquisitions.filter(a => a.inDomain);
        const optimalAcq = domainAcquisitions.length > 0 
          ? domainAcquisitions.reduce((optimal, current) => 
              current.value > optimal.value ? current : optimal, domainAcquisitions[0])
          : null;
        
        // Normalize to fit in the lower 5-15% of canvas height
        const normalizeAcq = (val) => {
          // Map to range [0,1]
          const normalized = (val - minAcq) / (maxAcq - minAcq || 1);
          // Map to 5-15% of canvas height from bottom
          return height * (0.95 - normalized * 0.1);
        };
        
        // Draw acquisition function
        ctx.strokeStyle = 'rgba(0, 180, 0, 0.7)';
        ctx.lineWidth = 2;
        ctx.beginPath();
        
        acquisitions.forEach((acq, i) => {
          const x = acq.x * width;
          const y = normalizeAcq(acq.value);
          
          if (i === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        });
        ctx.stroke();
        
        // Draw star at the optimum of the acquisition function (only if within domain)
        if (optimalAcq) {
          const optX = optimalAcq.x * width;
          const optY = normalizeAcq(optimalAcq.value);
          
          // Use green for acquisition function optimum
          drawStar(ctx, optX, optY, 8, 'rgba(0, 180, 0, 0.9)', 'rgba(0, 0, 0, 0.5)');
          
          // Remove all annotation text and line for the acquisition function optimum
          // Just keep the star for simplicity
        }
      }
      
      // Draw sampled points
      sampledPoints.forEach((point, index) => {
        const x = point.x * width;
        const y = height * (1 - point.y);
        
        // Is this the latest point?
        const isLatest = index === sampledPoints.length - 1;
        
        // Draw point
        ctx.fillStyle = isLatest ? '#ff3333' : '#ff7777';
        ctx.beginPath();
        ctx.arc(x, y, isLatest ? 6 : 4, 0, 2 * Math.PI);
        ctx.fill();
        
        // Draw border
        ctx.strokeStyle = 'white';
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.arc(x, y, isLatest ? 6 : 4, 0, 2 * Math.PI);
        ctx.stroke();
      });
    }
    
    // Draw status messages for optimization modes
    if (mode === 'completed') {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
      ctx.font = '14px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('Optimization completed with ' + sampledPoints.length + ' samples', width / 2, 25);
      if (sampledPoints.length > 0) {
        const bestPoint = [...sampledPoints].sort((a, b) => b.y - a.y)[0];
        ctx.fillText(`Best value: ${bestPoint.y.toFixed(3)} at x = ${bestPoint.x.toFixed(3)}`, width / 2, 50);
      }
    }
  }, [mode, drawnFunction, sampledPoints, hyperparams]); // eslint-disable-line
  
  // Handle canvas resizing
  useEffect(() => {
    const handleResize = () => {
      const canvas = canvasRef.current;
      const container = containerRef.current;
      if (!canvas || !container) return;
      
      const width = container.clientWidth;
      const height = Math.min(Math.max(width * 0.5, 250), 400);
      
      canvas.width = width;
      canvas.height = height;
    };
    
    window.addEventListener('resize', handleResize);
    handleResize();
    
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Cleanup on unmount
  useEffect(() => {
    const currentTimer = optimizationTimerRef.current;
    return () => {
      if (currentTimer) {
        clearTimeout(currentTimer);
      }
    };
  });
  
  // Implement the acquisition functions
  const calculateAcquisition = (x, sampledPoints, hyperparams, method) => {
    const { mean, variance } = predictGP(x, sampledPoints, hyperparams);
    const stdDev = Math.sqrt(Math.max(0.001, variance));
    
    // Find the best observed value so far
    const bestY = sampledPoints.length > 0 
      ? Math.max(...sampledPoints.map(p => p.y)) 
      : 0;
    
    switch (method) {
      case 'UCB':
        // Upper Confidence Bound
        const beta = 2.0 + Math.log(1.0 + sampledPoints.length * 0.4);
        return mean + beta * stdDev;
        
      case 'EI':
        // Expected Improvement
        if (stdDev < 1e-6) return 0; // Avoid division by zero
        
        const z = (mean - bestY) / stdDev;
        // Standard normal CDF and PDF
        const cdf = 0.5 * (1 + Math.erf(z / Math.sqrt(2)));
        const pdf = Math.exp(-0.5 * z * z) / Math.sqrt(2 * Math.PI);
        
        // Expected improvement
        return (mean - bestY) * cdf + stdDev * pdf;
        
      case 'qLogEI':
        // q-Log Expected Improvement (default)
        if (stdDev < 1e-6) return -Infinity; // Avoid division by zero
        
        const improvement = mean - bestY;
        const z2 = improvement / stdDev;
        
        // Standard normal CDF and PDF
        const cdf2 = 0.5 * (1 + Math.erf(z2 / Math.sqrt(2)));
        const pdf2 = Math.exp(-0.5 * z2 * z2) / Math.sqrt(2 * Math.PI);
        
        // Expected improvement
        const ei = improvement * cdf2 + stdDev * pdf2;
        
        // Log transform with small epsilon to avoid log(0)
        const epsilon = 1e-10;
        return Math.log(Math.max(ei, epsilon));
        
      default:
        return mean + 2.0 * stdDev; // Default to simple UCB
    }
  };
  
  // Math.erf implementation (not built into some browsers)
  if (!Math.erf) {
    Math.erf = function(x) {
      // Constants
      const a1 =  0.254829592;
      const a2 = -0.284496736;
      const a3 =  1.421413741;
      const a4 = -1.453152027;
      const a5 =  1.061405429;
      const p  =  0.3275911;
      
      // Save the sign
      const sign = x < 0 ? -1 : 1;
      x = Math.abs(x);
      
      // Approximation formula
      const t = 1.0 / (1.0 + p * x);
      const y = 1.0 - (((((a5 * t + a4) * t) + a3) * t + a2) * t + a1) * t * Math.exp(-x * x);
      
      return sign * y;
    };
  }
  
  // Add a specific effect for initial rendering
  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;
    
    // Ensure canvas is sized properly
    const width = container.clientWidth;
    const height = Math.min(Math.max(width * 0.5, 250), 400);
    
    canvas.width = width;
    canvas.height = height;
    
    // Draw initial instructions
    const ctx = canvas.getContext('2d');
    
    // Clear canvas
    ctx.fillStyle = '#fcfcfc';
    ctx.fillRect(0, 0, width, height);
    
    // Draw prominent instructions
    ctx.fillStyle = 'rgba(0, 0, 0, 0.8)';
    ctx.font = 'bold 16px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('Draw a function by clicking', width / 2, height / 2 - 30);
    
    // Visual hint - drawing arrow
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.4)';
    ctx.lineWidth = 2;
    ctx.setLineDash([5, 3]);
    
    // Draw a wavy line to suggest drawing
    ctx.beginPath();
    const startX = width * 0.3;
    const endX = width * 0.7;
    const midY = height / 2 + 30;
    const waveHeight = 20;
    
    ctx.moveTo(startX, midY);
    for (let x = startX; x <= endX; x += 10) {
      const progress = (x - startX) / (endX - startX);
      const y = midY + Math.sin(progress * Math.PI * 3) * waveHeight;
      ctx.lineTo(x, y);
    }
    ctx.stroke();
    
    // Reset line dash
    ctx.setLineDash([]);
  }, []); // Empty dependency array ensures this runs only once on mount
  
  // Helper function to draw a star
  const drawStar = (ctx, x, y, radius, fillColor, strokeColor) => {
    const innerRadius = radius * 0.4;
    const spikes = 5;
    
    ctx.fillStyle = fillColor;
    ctx.strokeStyle = strokeColor;
    ctx.lineWidth = 1;
    
    ctx.beginPath();
    for (let i = 0; i < spikes * 2; i++) {
      const r = i % 2 === 0 ? radius : innerRadius;
      const angle = (Math.PI / spikes) * i;
      const pointX = x + r * Math.sin(angle);
      const pointY = y + r * Math.cos(angle);
      
      if (i === 0) ctx.moveTo(pointX, pointY);
      else ctx.lineTo(pointX, pointY);
    }
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
  };
  
  return (
    <div className="gp-demo-outer" ref={containerRef}>
      <canvas 
        ref={canvasRef} 
        className="gp-canvas"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        // Add touch events for mobile
        onTouchStart={(e) => {
          e.preventDefault();
          const touch = e.touches[0];
          handleMouseDown({clientX: touch.clientX, clientY: touch.clientY});
        }}
        onTouchMove={(e) => {
          e.preventDefault();
          const touch = e.touches[0];
          handleMouseMove({clientX: touch.clientX, clientY: touch.clientY});
        }}
        onTouchEnd={(e) => {
          e.preventDefault();
          handleMouseUp();
        }}
      />
      <div className="gp-controls">
        {mode === 'drawing' && (
          <button 
            className="gp-button gp-start-button" 
            onClick={startOptimization}
            disabled={drawnFunction.length < 10}
          >
            Start Optimization
          </button>
        )}
        
        {mode === 'waiting' && (
          <button 
            className="gp-button gp-next-button" 
            onClick={addNextSample}
            disabled={sampledPoints.length >= MAX_SAMPLES}
          >
            Take Next Sample ({sampledPoints.length}/{MAX_SAMPLES})
          </button>
        )}
        <button 
          className="gp-button gp-reset-button" 
          onClick={resetDemo}
        >
          Reset
        </button>
        
        {(mode === 'optimizing' || mode === 'waiting' || mode === 'completed') && (
          <div className="gp-info">
            <div className="gp-legend">
              <div className="legend-item">
                <span className="legend-color" style={{backgroundColor: '#444444'}}></span>
                <span>True Function</span>
              </div>
              <div className="legend-item">
                <span className="legend-color" style={{backgroundColor: '#4169E1'}}></span>
                <span>GP Mean</span>
              </div>
              <div className="legend-item">
                <span className="legend-color" style={{backgroundColor: 'rgba(0, 180, 0, 0.7)'}}></span>
                <span>Acquisition</span>
              </div>
            </div>
          </div>
        )}
        
        {mode === 'completed' && (
          <div className="gp-message">
            Maximum number of samples ({MAX_SAMPLES}) reached.
          </div>
        )}  
      </div>
    </div>
  );
};

export default GaussianProcessDemo; 